import {
  CANONICAL_BOOKS,
  dedupeVerseTuples,
  getCanonicalBook,
  getChapterVerseCount,
  verseKeyToTuple,
  verseTupleToKey,
} from './canonical_verse_utils';

const aliasesByBook = [
  ['genesis', 'gen', 'ge', 'gn'],
  ['exodus', 'exodus', 'exod', 'exo', 'ex'],
  ['leviticus', 'leviticus', 'lev', 'lv'],
  ['numbers', 'numbers', 'num', 'nu', 'nm'],
  ['deuteronomy', 'deuteronomy', 'deut', 'dt'],
  ['joshua', 'joshua', 'josh', 'jos'],
  ['judges', 'judges', 'judg', 'jdg', 'jdgs'],
  ['ruth'],
  ['1 samuel', '1 sam', '1 sa', '1sam'],
  ['2 samuel', '2 sam', '2 sa', '2sam'],
  ['1 kings', '1 kgs', '1 king', '1 ki', '1kgs'],
  ['2 kings', '2 kgs', '2 king', '2 ki', '2kgs'],
  ['1 chronicles', '1 chron', '1 chr', '1 ch', '1chron'],
  ['2 chronicles', '2 chron', '2 chr', '2 ch', '2chron'],
  ['ezra', 'ezr'],
  ['nehemiah', 'neh'],
  ['esther', 'esth', 'est'],
  ['job'],
  ['psalms', 'psalm', 'psa', 'ps'],
  ['proverbs', 'prov', 'prv', 'pr'],
  ['ecclesiastes', 'eccl', 'ecc', 'ec'],
  ['song of songs', 'song of solomon', 'song', 'songs', 's s', 'ss', 'sos', 'canticles'],
  ['isaiah', 'isa', 'is'],
  ['jeremiah', 'jer', 'je'],
  ['lamentations', 'lam'],
  ['ezekiel', 'ezek', 'eze'],
  ['daniel', 'dan', 'dn'],
  ['hosea', 'hos'],
  ['joel', 'jl'],
  ['amos', 'am'],
  ['obadiah', 'obad', 'ob'],
  ['jonah', 'jon'],
  ['micah', 'mic'],
  ['nahum', 'nah'],
  ['habakkuk', 'hab'],
  ['zephaniah', 'zeph', 'zep'],
  ['haggai', 'hag'],
  ['zechariah', 'zech', 'zec'],
  ['malachi', 'mal'],
  ['matthew', 'matt', 'mt'],
  ['mark', 'mk'],
  ['luke', 'lk'],
  ['john', 'jn', 'joh'],
  ['acts', 'act'],
  ['romans', 'rom', 'ro'],
  ['1 corinthians', '1 cor', '1 co', '1cor'],
  ['2 corinthians', '2 cor', '2 co', '2cor'],
  ['galatians', 'gal'],
  ['ephesians', 'eph'],
  ['philippians', 'phil', 'php'],
  ['colossians', 'col'],
  ['1 thessalonians', '1 thes', '1 thess', '1 th', '1thes'],
  ['2 thessalonians', '2 thes', '2 thess', '2 th', '2thes'],
  ['1 timothy', '1 tim', '1 ti', '1tim'],
  ['2 timothy', '2 tim', '2 ti', '2tim'],
  ['titus', 'tit'],
  ['philemon', 'philem', 'phlm', 'phm'],
  ['hebrews', 'heb'],
  ['james', 'jas', 'jm'],
  ['1 peter', '1 pet', '1 pe', '1 pt', '1pet'],
  ['2 peter', '2 pet', '2 pe', '2 pt', '2pet'],
  ['1 john', '1 jn', '1 joh', '1john'],
  ['2 john', '2 jn', '2 joh', '2john'],
  ['3 john', '3 jn', '3 joh', '3john'],
  ['jude', 'jud'],
  ['revelation', 'rev', 'revelations', 'apocalypse'],
];

function normalizeBookAlias(value) {
  return value
    .toLowerCase()
    .replace(/\./g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export const BOOK_ALIAS_TO_NUMBER = Object.freeze(
  aliasesByBook.reduce((result, aliases, index) => {
    aliases.forEach(alias => {
      result[normalizeBookAlias(alias)] = index + 1;
    });
    return result;
  }, {})
);

function referenceError(reference, message) {
  return new RangeError(`Invalid verse reference "${reference}": ${message}`);
}

function createTuple(bookNumber, chapter, verse, reference) {
  const book = getCanonicalBook(bookNumber);
  if (!Number.isInteger(chapter) || chapter < 1 || chapter > book.verseCounts.length) {
    throw referenceError(
      reference,
      `${book.name} has chapters 1-${book.verseCounts.length}; received chapter ${chapter}`
    );
  }
  const lastVerse = getChapterVerseCount(bookNumber, chapter);
  if (!Number.isInteger(verse) || verse < 1 || verse > lastVerse) {
    throw referenceError(
      reference,
      `${book.name} ${chapter} has verses 1-${lastVerse}; received verse ${verse}`
    );
  }
  return [bookNumber, chapter, verse];
}

function expandChapter(bookNumber, chapter, reference) {
  const lastVerse = getChapterVerseCount(bookNumber, chapter);
  return Array.from(
    { length: lastVerse },
    (_, index) => createTuple(bookNumber, chapter, index + 1, reference)
  );
}

function expandVerseRange(start, end, reference) {
  if (start[0] !== end[0]) {
    throw referenceError(reference, 'ranges cannot span books');
  }
  const startKey = verseTupleToKey(start);
  const endKey = verseTupleToKey(end);
  if (endKey < startKey) {
    throw referenceError(reference, 'range end precedes range start');
  }
  return Array.from(
    { length: endKey - startKey + 1 },
    (_, index) => verseKeyToTuple(startKey + index)
  );
}

function parseChapterExpression(bookNumber, expression, reference) {
  const result = [];
  let lastChapter;
  expression.split(',').forEach(part => {
    const match = part.match(/^(\d+)(?:-(\d+))?$/);
    if (!match) {
      throw referenceError(reference, `invalid chapter list "${expression}"`);
    }
    const start = Number(match[1]);
    const end = match[2] ? Number(match[2]) : start;
    if (end < start) {
      throw referenceError(reference, 'chapter range end precedes range start');
    }
    for (let chapter = start; chapter <= end; chapter += 1) {
      result.push(...expandChapter(bookNumber, chapter, reference));
      lastChapter = chapter;
    }
  });
  return { tuples: result, chapter: lastChapter };
}

function parseEndpoint(value, inheritedChapter, bookNumber, reference) {
  const match = value.match(/^(?:(\d+):)?(\d+)$/);
  if (!match) {
    throw referenceError(reference, `invalid verse endpoint "${value}"`);
  }
  const chapter = match[1] ? Number(match[1]) : inheritedChapter;
  if (!chapter) {
    throw referenceError(reference, 'a chapter is required before a verse number');
  }
  return createTuple(bookNumber, chapter, Number(match[2]), reference);
}

function parseVerseExpression(bookNumber, expression, initialChapter, reference) {
  const result = [];
  let chapter = initialChapter;
  expression.split(',').forEach(part => {
    const pieces = part.split('-');
    if (pieces.length > 2 || pieces.some(piece => piece === '')) {
      throw referenceError(reference, `invalid verse range "${part}"`);
    }
    const start = parseEndpoint(pieces[0], chapter, bookNumber, reference);
    const end = pieces.length === 2
      ? parseEndpoint(pieces[1], start[1], bookNumber, reference)
      : start;
    result.push(...expandVerseRange(start, end, reference));
    chapter = end[1];
  });
  return { tuples: result, chapter };
}

function extractBook(segment) {
  const parts = segment.trim().split(/\s+/);
  for (let boundary = parts.length - 1; boundary >= 1; boundary -= 1) {
    const bookNumber = BOOK_ALIAS_TO_NUMBER[
      normalizeBookAlias(parts.slice(0, boundary).join(' '))
    ];
    if (bookNumber) {
      return {
        bookNumber,
        body: parts.slice(boundary).join(' '),
      };
    }
  }
  return null;
}

function normalizeExpression(body, reference) {
  const normalized = body
    .replace(/(\d+)[a-f]\b/gi, '$1')
    .replace(/\s+/g, '');
  if (!normalized || !/^[\d,:-]+$/.test(normalized)) {
    throw referenceError(reference, `unsupported reference syntax "${body}"`);
  }
  return normalized;
}

export function parseVerseReferences(input) {
  if (typeof input !== 'string' || !input.trim()) {
    throw new TypeError('Verse reference input must be a non-empty string');
  }

  const originalInput = input;
  const segments = input
    .replace(/[–—]/g, '-')
    .split(';')
    .map(segment => segment.replace(/^\s*cf\.\s*/i, '').trim());

  if (segments.some(segment => !segment)) {
    throw referenceError(originalInput, 'empty semicolon-separated reference');
  }

  const context = {
    bookNumber: null,
    chapter: null,
    mode: null,
  };
  const tuples = [];

  segments.forEach(segment => {
    const explicitBook = extractBook(segment);
    let body = explicitBook ? explicitBook.body : segment;
    if (explicitBook) {
      context.bookNumber = explicitBook.bookNumber;
      context.chapter = null;
      context.mode = null;
    }
    if (!context.bookNumber) {
      throw referenceError(originalInput, `book is missing from "${segment}"`);
    }

    const shorthand = body.match(/^v(v)?\.?\s*(.+)$/i);
    if (shorthand) {
      if (
        !context.chapter
        && getCanonicalBook(context.bookNumber).verseCounts.length === 1
      ) {
        context.chapter = 1;
      }
      if (!context.chapter) {
        throw referenceError(originalInput, `chapter is missing before "${segment}"`);
      }
      body = shorthand[2];
    }

    const expression = normalizeExpression(body, originalInput);
    const book = getCanonicalBook(context.bookNumber);
    const hasVerseSyntax = expression.includes(':');
    const singleChapterVerseList = book.verseCounts.length === 1
      && expression !== '1'
      && (expression.includes(',') || expression.includes('-') || Number(expression) !== 1);
    const inheritedVerseList = !explicitBook && context.mode === 'verse';
    const parsesVerses = Boolean(
      shorthand
      || hasVerseSyntax
      || singleChapterVerseList
      || inheritedVerseList
    );

    if (parsesVerses) {
      const parsed = parseVerseExpression(
        context.bookNumber,
        expression,
        context.chapter || (book.verseCounts.length === 1 ? 1 : null),
        originalInput
      );
      tuples.push(...parsed.tuples);
      context.chapter = parsed.chapter;
      context.mode = 'verse';
    } else {
      const parsed = parseChapterExpression(context.bookNumber, expression, originalInput);
      tuples.push(...parsed.tuples);
      context.chapter = parsed.chapter;
      context.mode = 'chapter';
    }
  });

  return dedupeVerseTuples(tuples);
}

export function formatVerseTuple(tuple) {
  const [bookNumber, chapter, verse] = tuple;
  const book = getCanonicalBook(bookNumber);
  createTuple(bookNumber, chapter, verse, JSON.stringify(tuple));
  const name = book.name
    .split(' ')
    .map((part, index) => (
      index > 0 && part === 'of'
        ? part
        : `${part.charAt(0).toUpperCase()}${part.slice(1)}`
    ))
    .join(' ');
  return `${name} ${chapter}:${verse}`;
}

export function getBookNumber(bookNameOrAlias) {
  const bookNumber = BOOK_ALIAS_TO_NUMBER[normalizeBookAlias(String(bookNameOrAlias || ''))];
  if (!bookNumber) {
    throw new RangeError(`Unknown Bible book: ${bookNameOrAlias}`);
  }
  return bookNumber;
}

export function getBookAliases(bookNumber) {
  getCanonicalBook(bookNumber);
  return [...aliasesByBook[bookNumber - 1]];
}

export function getCanonicalBookNames() {
  return CANONICAL_BOOKS.map(book => book.name);
}
